import { MessageStatus } from 'src/api/generated';
import type { WhatsAppTemplateMutationCallbacks } from 'src/modules/shared/messaging/types/whatsapp-template-send.types';
import { beginMessageMutationOrdering } from 'src/modules/shared/realtime/communications/utils/cache/event-order.util';
import {
  getChannelSessionQueryParams,
  parseFilterByExpression,
} from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import type { ConversationTemplateMutationState } from '../../types/whatsapp/template-mutation.types';
import { cancelMessageMutationReads } from './message-mutation-reads.util';
import { createWhatsAppMessageCacheUpdate } from './whatsapp-update.util';

/** Communications owns attribution, ordering, and targeted cache effects. */
export const conversationTemplateMutationCallbacks: WhatsAppTemplateMutationCallbacks<ConversationTemplateMutationState> =
  {
    onMutate: (context) => {
      const state = {
        ...context,
        interruptedInitialReads: cancelMessageMutationReads(context),
        messageOrdering: context.optimisticMessage
          ? beginMessageMutationOrdering(
              context.queryClient,
              context.optimisticMessage
            )
          : undefined,
      };
      if (
        context.params.optimisticData?.funnelConversationId &&
        context.optimisticMessage
      ) {
        createWhatsAppMessageCacheUpdate(context.optimisticMessage, state);
      }
      return state;
    },
    onSuccess: (message, context, state) => {
      createWhatsAppMessageCacheUpdate(message, state ?? context);
      const conversationId =
        message.funnelConversationId ??
        context.params.optimisticData?.funnelConversationId;
      if (conversationId) {
        void context.queryClient.invalidateQueries({
          predicate: (query) =>
            query.queryKey[0] === 'crm/funnel-channel-sessions' &&
            parseFilterByExpression(
              getChannelSessionQueryParams(query.queryKey)?.filterBy
            ).funnelConversationId === String(conversationId),
        });
      }
    },
    onError: (_error, context, state) => {
      if (
        context.params.optimisticData?.funnelConversationId &&
        context.optimisticMessage
      ) {
        createWhatsAppMessageCacheUpdate(
          { ...context.optimisticMessage, status: MessageStatus.FAILED },
          state ?? context
        );
      }
    },
    onSettled: (context, state) => {
      state?.messageOrdering?.dispose();
      void context.queryClient.invalidateQueries({
        predicate: (query) =>
          Boolean(state?.interruptedInitialReads.includes(query)) &&
          query.state.data === undefined,
        refetchType: 'active',
      });
    },
  };
